import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert, Chip,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { phappsRequest } from "@/services/apiService";
import { buildDialpadPath } from "@/config/apiConstants";
import AllocateCallModal from "./AllocateCallModal";
import CallDetailModal from "./CallDetailModal";

const DIALPAD_PINK = "#E63888";

function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

const CardCallsTab = ({ context, cardId }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [openCall, setOpenCall] = useState(null);

  const load = useCallback(async () => {
    if (!cardId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await phappsRequest(buildDialpadPath(`calls/by-card/${cardId}`), "GET");
      setCalls(data ?? []);
    } catch {
      setError("Failed to load calls");
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
          {calls.length > 0 ? `${calls.length} call${calls.length !== 1 ? "s" : ""} linked` : "No calls linked yet"}
        </Typography>
        <Button
          size="small"
          startIcon={<PhoneOutlinedIcon />}
          onClick={() => setAllocateOpen(true)}
          sx={{ textTransform: "none", fontWeight: 600, color: DIALPAD_PINK, borderColor: DIALPAD_PINK }}
          variant="outlined"
        >
          Allocate Call
        </Button>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: DIALPAD_PINK }} />
        </Box>
      ) : calls.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <PhoneOutlinedIcon sx={{ fontSize: 36, color: "#d1d5db", mb: 1 }} />
          <Typography color="text.secondary" variant="body2">
            No calls have been linked to this card yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {calls.map(call => (
            <Box
              key={call.id}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 1.5, borderRadius: 1.5,
                border: "1px solid #e5e7eb", backgroundColor: "#fff",
                "&:hover": { borderColor: DIALPAD_PINK, backgroundColor: "#FDE8F3" },
                transition: "all 0.12s",
              }}
            >
              <Box sx={{ color: call.direction === "inbound" ? "#059669" : "#2563eb", flexShrink: 0 }}>
                {call.direction === "inbound" ? <CallReceivedIcon fontSize="small" /> : <CallMadeIcon fontSize="small" />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {call.external_number || "Unknown"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {fmtDateTime(call.start_time)} · {fmtDuration(call.duration_seconds)}
                  {call.manager_name ? ` · ${call.manager_name}` : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexShrink: 0 }}>
                {call.has_ai_summary && (
                  <Chip label="AI" size="small" sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "#ede9fe", color: DIALPAD_PINK }} />
                )}
                {call.auto_matched && (
                  <Chip label="Auto" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                )}
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={() => setOpenCall(call)}
                  sx={{ textTransform: "none", fontSize: "0.7rem", color: DIALPAD_PINK, minWidth: 0, px: 1 }}
                >
                  View
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <AllocateCallModal
        open={allocateOpen}
        onClose={() => setAllocateOpen(false)}
        context={context}
        cardId={cardId}
        onAllocated={() => { setAllocateOpen(false); load(); }}
      />

      <CallDetailModal
        open={!!openCall}
        onClose={() => setOpenCall(null)}
        call={openCall}
      />
    </Box>
  );
};

export default CardCallsTab;
